import "urlpattern-polyfill";
import type {RouteConfig} from "@lit-labs/router";
import {translateOr, type TranslateFn} from "../../helpers/translate-or.helper";

/**
 * The browser tab title, "<app name> – <view>" (feature plan 0067). Anke, 2026-10-29: "könnten wir
 * das so machen, dass jeder mandant selbst einen namen vergeben kann?" Both parts are i18n texts in
 * the `pageTitle` namespace of de.json, so a tenant admin overrides them in the translation editor
 * (tab "App-Name und Seitentitel"); `pageTitle.<route name>` names a view.
 *
 * Pure on purpose: ha-app itself cannot be loaded in vitest (router, lottie, DI graph), so ha-app
 * only hands in the pathname, the routes and lit-translate's `get`.
 */

/** The app name when `pageTitle.app` has no usable text — the title the shell always had. */
export const DEFAULT_APP_TITLE = 'VA Horst';

const SEPARATOR = ' – ';

// One pattern per route, like the router's own WeakMap, so a re-render does not rebuild them.
const patterns = new WeakMap<RouteConfig, URLPattern>();

/**
 * The name of the route the router renders for `pathname`: the first route whose pattern matches,
 * in list order — the router's own rule (`getPattern` and `_getRoute` in `@lit-labs/router`
 * routes.js), including routes given as `pattern:`.
 */
export function routeNameFor(pathname: string, routes: RouteConfig[]): string | undefined {
    for (const route of routes) {
        let pattern = patterns.get(route);
        if (!pattern) {
            pattern = 'pattern' in route ? route.pattern : new URLPattern({pathname: route.path});
            patterns.set(route, pattern);
        }
        if (pattern.test({pathname})) {
            return route.name;
        }
    }
    return undefined;
}

export interface PageTitleInput {
    isLoggedIn: boolean;
    pathname: string;
    routes: RouteConfig[];
    translate: TranslateFn;
}

/**
 * "<app name> – <view>"; only the app name when logged out (the login form covers every route) or
 * when the route has no usable `pageTitle.<name>` text (redirects, `login`, `fallback`).
 */
export function pageTitleFor({isLoggedIn, pathname, routes, translate}: PageTitleInput): string {
    const app = translateOr('pageTitle.app', DEFAULT_APP_TITLE, undefined, translate);
    if (!isLoggedIn) {
        return app;
    }
    const name = routeNameFor(pathname, routes);
    const view = name === undefined ? '' : translateOr(`pageTitle.${name}`, '', undefined, translate);
    return view === '' ? app : `${app}${SEPARATOR}${view}`;
}
